import { useEffect, useMemo, useRef, useState } from "react";
import type { CalendarEvent, CalendarEventInput } from "../../api";
import type { DealSummary } from "@edge/shared";

interface Props {
  existing?: CalendarEvent;
  /** When creating a new event (no `existing`), pre-fill start with this ISO.
   *  End defaults to start + 30 min. Used by the week/month view click-empty
   *  flow to drop the user into a form anchored at the clicked time/date. */
  initialStart?: string;
  initialAllDay?: boolean;
  deals?: DealSummary[];
  onSave: (input: CalendarEventInput) => Promise<void>;
  onCancel: () => void;
}

/**
 * Create/edit form for a one-time calendar event. Phase C.1 — no recurrence
 * controls yet (those land in C.2). The date+time fields use native HTML5
 * `datetime-local` inputs; we convert to ISO UTC before submitting.
 *
 * "All day" toggle hides the time portion; events span the full day in PT.
 */
export function CalendarEventForm({
  existing,
  initialStart,
  initialAllDay,
  deals,
  onSave,
  onCancel,
}: Props) {
  const [title, setTitle] = useState(existing?.title ?? "");
  const [body, setBody] = useState(existing?.body ?? "");
  const [location, setLocation] = useState(existing?.location ?? "");
  const seedStart = existing?.startAt ?? initialStart ?? defaultStart();
  const seedEnd =
    existing?.endAt ??
    (initialStart
      ? new Date(new Date(initialStart).getTime() + 30 * 60_000).toISOString()
      : defaultEnd());
  const [startLocal, setStartLocal] = useState(isoToLocalInput(seedStart));
  const [endLocal, setEndLocal] = useState(isoToLocalInput(seedEnd));
  const [allDay, setAllDay] = useState(existing?.allDay ?? initialAllDay ?? false);
  const [priority, setPriority] = useState<"hi" | "normal" | "lo">(
    existing?.priority ?? "normal",
  );
  const [dealId, setDealId] = useState<string>(existing?.dealId ?? "");
  const [leadTimeDays, setLeadTimeDays] = useState<number>(existing?.leadTimeDays ?? 7);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tracked duration between start and end. Initialized from the existing
  // event (edit case) or 30 min for new events. Updated whenever the user
  // manually changes the end — locks in their intent for subsequent
  // start-change auto-shifts. Ref instead of state: read-only during render.
  const durationMsRef = useRef<number>(
    existing
      ? Math.max(
          0,
          new Date(existing.endAt).getTime() - new Date(existing.startAt).getTime(),
        )
      : 30 * 60_000,
  );
  // Snapshot of timed start/end before flipping all-day ON so we can restore
  // on flip OFF without forcing the user to retype.
  const beforeAllDayRef = useRef<{ start: string; end: string } | null>(null);

  /** Bounded picker range: today-1y to today+5y, so the year scroller doesn't
   *  jump to 1700. Separate values for type="date" (10-char) vs
   *  type="datetime-local" (16-char with T00:00). Chrome silently degrades
   *  the dropdown picker when min/max format doesn't match the input type. */
  const pickerRange = useMemo(() => {
    const now = new Date();
    const minDate = new Date(now.getFullYear() - 1, 0, 1).toISOString().slice(0, 10);
    const maxDate = new Date(now.getFullYear() + 5, 11, 31).toISOString().slice(0, 10);
    return {
      minDate,
      maxDate,
      minDatetime: `${minDate}T00:00`,
      maxDatetime: `${maxDate}T23:59`,
    };
  }, []);

  /** Programmatically open the native picker on any click in the input.
   *  Without this, Chrome desktop only opens the dropdown when the user
   *  clicks the tiny calendar icon at the right edge — clicking the text
   *  drops them into manual-edit mode. showPicker() is the official Web
   *  API for this; wrapped in try/catch because some browsers throw if
   *  the input isn't fully ready or focus is elsewhere. */
  const openPicker = (e: React.MouseEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    try {
      input.showPicker?.();
    } catch {
      /* fall back to default behavior — user can still click the icon */
    }
  };

  // If user types a custom title hint that matches a deal, auto-suggest. Optional UX polish:
  const dealOptions = useMemo(
    () => (deals ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [deals],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  /** Auto-shift end when start changes, preserving the tracked duration. */
  const handleStartChange = (newStart: string) => {
    setStartLocal(newStart);
    if (allDay) {
      // All-day: end-date follows start-date (one-day default until the user
      // explicitly changes end). Date portion only.
      setEndLocal(newStart.slice(0, 10));
      return;
    }
    // Compute new end = new start + locked duration. Skip if parse fails
    // (intermediate keystroke states like "2026-05-" — let the user finish typing).
    const newStartMs = localInputToMs(newStart, false);
    if (newStartMs === null) return;
    setEndLocal(msToLocalInput(newStartMs + durationMsRef.current, false));
  };

  /** Manual end change locks in a new duration for future start auto-shifts. */
  const handleEndChange = (newEnd: string) => {
    setEndLocal(newEnd);
    if (allDay) return;
    const newEndMs = localInputToMs(newEnd, false);
    const startMs = localInputToMs(startLocal, false);
    if (newEndMs === null || startMs === null) return;
    const delta = newEndMs - startMs;
    if (delta > 0) durationMsRef.current = delta;
  };

  /** All-day toggle: snapshot timed values on ON, restore on OFF. */
  const handleAllDayToggle = (next: boolean) => {
    if (next && !allDay) {
      // OFF → ON: save current timed values; snap end to start's date so the
      // all-day input renders a single-day event by default.
      beforeAllDayRef.current = { start: startLocal, end: endLocal };
      setEndLocal(startLocal.slice(0, 10));
    } else if (!next && allDay) {
      // ON → OFF: restore prior timed values if we have them, else defaults.
      const prior = beforeAllDayRef.current;
      if (prior) {
        setStartLocal(prior.start);
        setEndLocal(prior.end);
      } else {
        setStartLocal(isoToLocalInput(defaultStart()));
        setEndLocal(isoToLocalInput(defaultEnd()));
      }
    }
    setAllDay(next);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    const startUTC = localInputToISO(startLocal, allDay, "start");
    const endUTC = localInputToISO(endLocal, allDay, "end");
    if (!startUTC || !endUTC) {
      setError("Start and end are required.");
      return;
    }
    if (new Date(endUTC).getTime() < new Date(startUTC).getTime()) {
      setError("End must be after start.");
      return;
    }

    const dealName = dealId
      ? (deals ?? []).find((d) => d.id === dealId)?.name ?? null
      : null;

    setSubmitting(true);
    try {
      await onSave({
        title: title.trim(),
        body: body.trim() || null,
        location: location.trim() || null,
        startAt: startUTC,
        endAt: endUTC,
        allDay,
        priority,
        dealId: dealId || null,
        dealName,
        leadTimeDays,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "save failed");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="write-confirm-backdrop nested-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="calendar-form-card" onClick={(e) => e.stopPropagation()}>
        <div className="calendar-modal-header">
          <div className="calendar-modal-title">
            {existing ? "EDIT EVENT" : "NEW EVENT"}
          </div>
          <button className="calendar-modal-close" onClick={onCancel} aria-label="Cancel">
            ✕
          </button>
        </div>

        <div className="calendar-form-body">
          <label className="calendar-form-row">
            <span className="calendar-form-label">TITLE</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Tax season reminder"
              autoFocus
              maxLength={200}
            />
          </label>

          <label className="calendar-form-row checkbox">
            <input
              type="checkbox"
              checked={allDay}
              onChange={(e) => handleAllDayToggle(e.target.checked)}
            />
            <span>ALL DAY</span>
          </label>

          <div className="calendar-form-row split">
            <label className="calendar-form-half">
              <span className="calendar-form-label">
                <span className="cal-icon" aria-hidden>📅</span> START
              </span>
              <input
                className="calendar-datetime-input"
                type={allDay ? "date" : "datetime-local"}
                value={allDay ? startLocal.slice(0, 10) : startLocal}
                min={allDay ? pickerRange.minDate : pickerRange.minDatetime}
                max={allDay ? pickerRange.maxDate : pickerRange.maxDatetime}
                onClick={openPicker}
                onChange={(e) => handleStartChange(e.target.value)}
              />
            </label>
            <label className="calendar-form-half">
              <span className="calendar-form-label">
                <span className="cal-icon" aria-hidden>📅</span> END
              </span>
              <input
                className="calendar-datetime-input"
                type={allDay ? "date" : "datetime-local"}
                value={allDay ? endLocal.slice(0, 10) : endLocal}
                min={allDay ? pickerRange.minDate : pickerRange.minDatetime}
                max={allDay ? pickerRange.maxDate : pickerRange.maxDatetime}
                onClick={openPicker}
                onChange={(e) => handleEndChange(e.target.value)}
              />
            </label>
          </div>

          <label className="calendar-form-row">
            <span className="calendar-form-label">LOCATION</span>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="optional"
              maxLength={200}
            />
          </label>

          <div className="calendar-form-row split">
            <label className="calendar-form-half">
              <span className="calendar-form-label">PRIORITY</span>
              <select
                value={priority}
                onChange={(e) =>
                  setPriority(e.target.value as "hi" | "normal" | "lo")
                }
              >
                <option value="hi">HI</option>
                <option value="normal">NORMAL</option>
                <option value="lo">LO</option>
              </select>
            </label>
            <label className="calendar-form-half">
              <span className="calendar-form-label">DEAL (optional)</span>
              <select value={dealId} onChange={(e) => setDealId(e.target.value)}>
                <option value="">— none —</option>
                {dealOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="calendar-form-row">
            <span className="calendar-form-label">
              ALERT LEAD TIME (days) — Watchtower (C.2)
            </span>
            <input
              type="number"
              min={0}
              max={365}
              value={leadTimeDays}
              onChange={(e) => setLeadTimeDays(Number(e.target.value))}
            />
          </label>

          <label className="calendar-form-row">
            <span className="calendar-form-label">NOTES</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="optional"
            />
          </label>

          {error && <div className="calendar-form-error">{error}</div>}
        </div>

        <div className="calendar-form-footer">
          <button onClick={onCancel} disabled={submitting} className="btn-secondary">
            CANCEL
          </button>
          <button onClick={handleSubmit} disabled={submitting} className="btn-primary">
            {submitting ? "SAVING…" : existing ? "SAVE CHANGES" : "CREATE EVENT"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Convenience wrappers around localInputToISO / isoToLocalInput in ms. Used
 *  by duration-tracking math. Returns null for unparseable intermediates. */
function localInputToMs(local: string, allDay: boolean): number | null {
  const iso = localInputToISO(local, allDay, "start");
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
}
function msToLocalInput(ms: number, _allDay: boolean): string {
  return isoToLocalInput(new Date(ms).toISOString());
}

/** Default start: today at next half-hour. */
function defaultStart(): string {
  const d = new Date();
  d.setMinutes(Math.ceil((d.getMinutes() + 1) / 30) * 30, 0, 0);
  return d.toISOString();
}
/** Default end: 30 min after default start. */
function defaultEnd(): string {
  const d = new Date(defaultStart());
  d.setMinutes(d.getMinutes() + 30);
  return d.toISOString();
}

/** Convert an ISO UTC timestamp to the "YYYY-MM-DDTHH:MM" string that the
 *  HTML5 `datetime-local` input expects, in Pacific time. */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  // Build a Pacific representation using Intl, then reassemble.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const map: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") map[p.type] = p.value;
  const hr = map.hour === "24" ? "00" : map.hour;
  return `${map.year}-${map.month}-${map.day}T${hr}:${map.minute}`;
}

/** Convert "YYYY-MM-DDTHH:MM" Pacific input to an ISO UTC string. For all-day
 *  events: start = local midnight, end = next-day midnight (full day span). */
function localInputToISO(
  local: string,
  allDay: boolean,
  which: "start" | "end",
): string | null {
  if (!local) return null;
  if (allDay) {
    const day = local.slice(0, 10);
    if (!day) return null;
    const [y, m, d] = day.split("-").map(Number);
    // Build Pacific-midnight epoch by interpreting the day in PT and adding
    // its UTC offset. Use a recent reference date for DST awareness.
    const ptMidnight = new Date(Date.UTC(y, m - 1, d, 8, 0, 0));
    // 8 UTC = midnight PDT; we'll let Date handle DST shifts via the locale.
    // For "end", advance one day to get an exclusive end-of-day boundary —
    // matches Outlook's all-day convention.
    if (which === "end") ptMidnight.setUTCDate(ptMidnight.getUTCDate() + 1);
    return ptMidnight.toISOString();
  }
  // datetime-local has no TZ; interpret as Pacific time.
  // Build a Date in PT by computing the offset for the given moment.
  const [datePart, timePart] = local.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  // Iteratively find the UTC instant that, when rendered in PT, matches the
  // input — simpler than wrangling DST offsets by hand.
  let guess = new Date(Date.UTC(y, mo - 1, d, h, mi, 0));
  for (let i = 0; i < 3; i++) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(guess);
    const map: Record<string, string> = {};
    for (const p of parts) if (p.type !== "literal") map[p.type] = p.value;
    const renderedHour = map.hour === "24" ? 0 : Number(map.hour);
    const renderedMinute = Number(map.minute);
    const driftMin =
      (renderedHour - h) * 60 + (renderedMinute - mi);
    if (driftMin === 0) break;
    guess = new Date(guess.getTime() - driftMin * 60_000);
  }
  return guess.toISOString();
}
